export const CHECKOUT_URL = 'https://complex-en.impultienda.ar/checkout/';

export const IMAGES = {
  productStack:
    'https://cdn.magicpatterns.com/uploads/kbZWJpYreEm5Es3SFUryZo/COMPLEX-EN.png',
  author:
    'https://cdn.magicpatterns.com/uploads/3TQAB2oMLTsAxMxhB7z1BX/Dra._Karito_Odontologa_OF.png',
  bonus1:
    'https://cdn.magicpatterns.com/uploads/irDnRMyEzC2ocpWrxVVx4T/BONUS_1_COMPLEX-EN.png',
  bonus2:
    'https://cdn.magicpatterns.com/uploads/jZPZtKjEBkWMzMozCTyMc2/BONUS_2_COMPLEX-EN.png',
  bonus3:
    'https://cdn.magicpatterns.com/uploads/dytYjmdQ8pSW8oo4qwd3cE/BONUS_3_COMPLEX-EN.png',
  bonus4:
    'https://cdn.magicpatterns.com/uploads/47kgKUKFFDJV1nzWfCRm5m/BONUS_4_COMPLEX-EN.png',
  bonus5:
    'https://cdn.magicpatterns.com/uploads/xfkP2Qu1s1XGhzggFF92pT/BONUS_5_COMPLEX-EN.png',
};

export const heroBullets = [
  'Severe tooth wear.',
  'Loss of tooth structure.',
  'Functional concerns.',
  'Extensive restorative needs.',
  'Possible changes in vertical dimension.',
  'Multiple occlusal variables.',
];

export const heroQuestions = [
  '“What should I review first?”',
  '“Which findings deserve more attention?”',
  '“Am I missing an important variable?”',
  '“What should I investigate before moving forward?”',
  '“Have I reviewed the case systematically?”',
];

export const beforeItems = [
  'You spend too much time going back through notes and references.',
  'You wonder where to begin with a complex case.',
  'You review variables inconsistently from case to case.',
  'You second-guess whether you have considered the important factors.',
  'You rely heavily on memory when reviewing complex cases.',
  'You worry that something important may deserve additional evaluation.',
];

export const afterItems = [
  'You have a defined 15-minute review sequence.',
  'You systematically review 7 critical areas.',
  'You identify clinical signals that deserve closer attention.',
  'You organize observations, questions and variables in one place.',
  'You document what still needs to be evaluated.',
  'You finish with a structured Case Readiness Review.',
];

export const painPoints = [
  {
    emoji: '🦷',
    title: '“I have a complex rehabilitation case… where do I even start?”',
    text: 'The patient presents with severe wear, loss of structure or extensive restorative needs, and you need a systematic way to begin reviewing the case.',
  },
  {
    emoji: '🤯',
    title: '“I know the theory… but applying everything together is different.”',
    text: 'You understand the concepts, but complex cases require you to bring multiple variables together without losing track of what deserves attention.',
  },
  {
    emoji: '📐',
    title: '“I’m still second-guessing my VDO review.”',
    text: 'You want a structured way to review the relevant variables rather than relying on one isolated measurement or intuition.',
  },
  {
    emoji: '🔍',
    title: '“What if I’m missing something?”',
    text: 'Complex rehabilitation cases contain many variables. Without a consistent review process, some questions may deserve further attention.',
  },
  {
    emoji: '⏱️',
    title: '“I keep going back to my notes.”',
    text: 'Books, lectures, saved posts and old course materials are scattered everywhere.',
  },
  {
    emoji: '💰',
    title: '“I want to feel more organized when reviewing complex cases.”',
    text: 'You know that a more consistent review process could help you approach complex rehabilitation planning with greater structure.',
  },
];

export const buildingBlocks = [
  'Case Classification',
  'Clinical Signals',
  'Critical Variables',
  'Risk Review',
  'Decision Review',
  'Case Readiness',
];

export interface Volume {
  numeral: string;
  title: string;
  headline: string;
  description: string;
  items: string[];
}

export const volumes: Volume[] = [
  {
    numeral: 'I',
    title: 'CASE CLASSIFIER™',
    headline: 'Know what kind of case you are dealing with before you start reviewing everything.',
    description:
      'A structured framework to help organize the initial complexity of a rehabilitation case.',
    items: [
      'Case Complexity Score',
      'Initial Case Snapshot',
      'Complexity Classification Map',
      'Chief Concern Review',
      'Key Information Checklist',
      'Initial Clinical Questions',
      'Case Type Worksheet',
      'Audit Preparation Checklist',
    ],
  },
  {
    numeral: 'II',
    title: 'DIAGNOSTIC SIGNAL AUDIT™',
    headline: 'Identify the findings that deserve a closer look.',
    description:
      'A structured system for organizing clinical signals before moving into deeper case planning.',
    items: [
      'Clinical Signal Scanner',
      'Diagnostic Review Map',
      'Signal Classification Matrix',
      'Functional Assessment Review',
      'Critical Finding Checklist',
      'Missing Information Tracker',
      'Clinical Question Sheet',
      'Diagnostic Audit Worksheet',
    ],
  },
  {
    numeral: 'III',
    title: 'VDO REVIEW AUDIT™',
    headline: 'Systematically review the variables surrounding vertical dimension.',
    description:
      'A visual framework designed to help organize the questions and variables that deserve consideration when reviewing VDO in complex rehabilitation cases.',
    items: [
      'VDO Review Map',
      'VDO Variable Matrix',
      'VDO Question Checklist',
      'Clinical Attention Signals',
      'Review Sequence',
      'Case Hypothesis Sheet',
      'VDO Documentation Worksheet',
      'VDO Quick Reference',
    ],
  },
  {
    numeral: 'IV',
    title: 'OCCLUSAL REVIEW AUDIT™',
    headline: 'Turn complex occlusal variables into a structured review process.',
    description:
      'Instead of trying to remember isolated concepts, organize your review into a repeatable sequence.',
    items: [
      'Occlusal Review Map',
      'Occlusal Variable Matrix',
      'Critical Review Checklist',
      'Functional-Occlusal Questions',
      'Clinical Signal Library',
      'Decision Review Tree',
      'Documentation Worksheet',
      'Occlusion Quick Reference',
    ],
  },
  {
    numeral: 'V',
    title: 'REHAB RISK AUDIT™',
    headline: 'Identify the areas that deserve additional attention before moving forward.',
    description:
      'A practical framework for organizing risk signals and prioritizing what requires further evaluation.',
    items: [
      'Risk Scanner',
      'Risk Classification System',
      'Clinical Red Flag Matrix',
      'Priority Matrix',
      'Missing Information Review',
      'Common Review Gaps',
      'Case Risk Summary',
      'Final Risk Review Sheet',
    ],
  },
  {
    numeral: 'VI',
    title: '15-MINUTE CASE READINESS™',
    headline: 'Bring the entire audit together in one final structured review.',
    description:
      'The complete 15-minute workflow for reviewing the major areas of a complex rehabilitation case.',
    items: [
      '15-Minute Audit Workflow',
      '7-Area Review Map',
      'Case Readiness Scorecard',
      'Critical Questions',
      'Missing Information Check',
      'Risk Review',
      'Final Case Summary',
      'Pre-Movement Audit',
      'One-Page Quick Reference',
    ],
  },
];

export const tools = [
  {
    emoji: '📝',
    title: 'AUDIT PROTOCOLS',
    text: 'Structured protocols to help you review different stages of a complex rehabilitation case.',
  },
  {
    emoji: '📋',
    title: 'PROFESSIONAL CHECKLISTS',
    text: 'Practical checklists designed to bring consistency to your case-review process.',
  },
  {
    emoji: '🧠',
    title: 'DECISION MAPS',
    text: 'Visual frameworks that help organize what question or variable deserves attention next.',
  },
  {
    emoji: '📊',
    title: 'RISK MATRICES',
    text: 'Organize clinical signals, priorities and areas requiring further evaluation in one place.',
  },
  {
    emoji: '🗂️',
    title: 'CASE WORKSHEETS',
    text: 'Document observations, questions, hypotheses and review points throughout the audit.',
  },
  {
    emoji: '⚡',
    title: 'QUICK REFERENCE TOOLS',
    text: 'Condensed visual references for fast consultation without searching through hours of notes.',
  },
];

export interface Bonus {
  label: string;
  title: string;
  subtitle: string;
  description: string;
  value: string;
  image: string;
}

export const bonuses: Bonus[] = [
  {
    label: 'BONUS #1',
    title: 'COMPLEX REHAB RED FLAGS™',
    subtitle: '50 Clinical Signals Worth a Closer Look',
    description:
      'A visual library of clinical signals organized into practical categories to help you identify findings that may deserve additional evaluation.',
    value: '$47',
    image: IMAGES.bonus1,
  },
  {
    label: 'BONUS #2',
    title: 'VDO REVIEW MASTER MAP™',
    subtitle: 'A Visual Framework for Organizing Your VDO Review',
    description:
      'A dedicated one-page reference map bringing key VDO variables and review questions together in one place.',
    value: '$37',
    image: IMAGES.bonus2,
  },
  {
    label: 'BONUS #3',
    title: '10 COMPLEX REHAB CASE CHALLENGES™',
    subtitle: 'Practice Your Case-Review Process',
    description:
      'Work through 10 complex rehabilitation scenarios using the audit framework and compare your review with the provided educational analysis.',
    value: '$47',
    image: IMAGES.bonus3,
  },
  {
    label: 'BONUS #4',
    title: 'CLINICAL AUDIT QUICK CARDS™',
    subtitle: '30 Rapid-Reference Review Cards',
    description:
      'Each card follows a simple framework: SIGNAL → QUESTION → VARIABLE → REVIEW → DOCUMENT. Designed for rapid reference when reviewing a complex case.',
    value: '$37',
    image: IMAGES.bonus4,
  },
  {
    label: 'BONUS #5',
    title: 'FINAL CASE READINESS CHECK™',
    subtitle: 'Your Last Review Before Moving Forward',
    description:
      'A structured final checklist designed to help you review whether the major areas of the case have been considered and whether additional information may still be needed.',
    value: '$29',
    image: IMAGES.bonus5,
  },
];

export const guaranteeSteps = [
  'Get immediate access.',
  'Open the six Audit Volumes.',
  'Review the 7-area framework.',
  'Run through the checklists and audit tools.',
  'Work through the case challenges.',
];

export interface Testimonial {
  quote: string;
  text: string;
  name: string;
  role: string;
  avatar: string;
}

export const testimonials: Testimonial[] = [
  {
    quote: 'It finally gave me a way to organize everything.',
    text: 'I’ve taken several courses on complex rehabilitation, but having a structured review sequence makes the information much easier to work through.',
    name: 'Dr. Michael Chen',
    role: 'Verified customer · Restorative Dentistry',
    avatar:
      'https://cdn.magicpatterns.com/patterns/generated-images/cca4ea14-84cb-4d1d-95a0-19c3bd22e441.jpg',
  },
  {
    quote: 'The 15-minute format makes it incredibly practical.',
    text: 'I don’t need to spend hours going through notes just to figure out what areas I should review.',
    name: 'Dr. Laura Medina',
    role: 'Verified customer · General Dentistry',
    avatar:
      'https://cdn.magicpatterns.com/patterns/generated-images/450de12b-7ad1-4d8b-af58-5f46f5287fb6.jpg',
  },
  {
    quote: 'The checklists are what I use the most.',
    text: 'They help me bring more consistency to the way I review complex cases.',
    name: 'Dr. Robert Hayes',
    role: 'Verified customer · Prosthodontics',
    avatar:
      'https://cdn.magicpatterns.com/patterns/generated-images/8b5587e9-fac4-49c9-99f2-d6ea84b61041.jpg',
  },
  {
    quote: 'It doesn’t feel like another ebook.',
    text: 'It feels like a professional case-review system that I can actually use.',
    name: 'Dr. Amara Silva',
    role: 'Verified customer · Rehabilitation',
    avatar:
      'https://cdn.magicpatterns.com/patterns/generated-images/136582f9-eaeb-4296-8b1d-e19128b248ff.jpg',
  },
  {
    quote: 'Everything is organized in one place.',
    text: 'The biggest difference is having a repeatable process instead of searching through different courses and notes every time.',
    name: 'Dr. Daniel Okafor',
    role: 'Verified customer · Occlusion & Function',
    avatar:
      'https://cdn.magicpatterns.com/patterns/generated-images/055d17c8-1ea8-48a5-898f-460686856ff5.jpg',
  },
];

export const faqs = [
  {
    q: 'Is Complex Rehab Case Audit™ a course?',
    a: 'No. It is a digital clinical case-review system created by Dr. Karito containing six Audit Volumes, clinical checklists, decision maps, matrices, worksheets, quick references and practical review tools.',
  },
  {
    q: 'Who is this for?',
    a: 'The system is designed primarily for dentists who evaluate or plan complex restorative and rehabilitation cases and want a more structured way to review the relevant variables.',
  },
  {
    q: 'Do I need to be a specialist?',
    a: 'No. The system is designed as an educational reference for dentists at different stages of professional experience. The appropriate use of any clinical framework depends on the dentist’s education, training and professional judgment.',
  },
  {
    q: 'Does this replace clinical education?',
    a: 'No. The Complex Rehab Case Audit™ is an educational and organizational tool. It does not replace formal education, continuing education, clinical training, professional judgment or patient-specific diagnosis and treatment planning.',
  },
  {
    q: 'How will I receive the product?',
    a: 'You will receive digital access after completing your purchase.',
  },
  {
    q: 'Can I download the materials?',
    a: 'The included digital materials are designed for convenient digital reference and, where applicable, printing for personal use.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. The offer is a one-time payment of $27.',
  },
  {
    q: 'Do I get lifetime access?',
    a: 'Yes, subject to the applicable access terms.',
  },
  {
    q: 'What happens if I don’t like it?',
    a: 'You have a 7-day money-back guarantee. If the product isn’t right for you, you can request a refund within the applicable 7-day period.',
  },
  {
    q: 'What currency will I be charged in?',
    a: 'The purchase amount is $27 USD. Currency conversion is automatic — your bank or the payment provider converts it to your local currency at checkout.',
  },
];

export const finalQuestions = [
  'WHAT SHOULD I REVIEW FIRST?',
  'WHICH VARIABLES DESERVE MORE ATTENTION?',
  'WHAT INFORMATION MAY STILL BE MISSING?',
  'WHAT RISKS OR SIGNALS SHOULD I REVIEW?',
  'HAVE I SYSTEMATICALLY REVIEWED THE CASE?',
];

export const purchaseNotifications = [
  { name: 'Dr. Camila R.', country: 'Mexico' },
  { name: 'Dr. Andrés M.', country: 'Colombia' },
  { name: 'Dr. Valentina S.', country: 'Argentina' },
  { name: 'Dr. Javier L.', country: 'Spain' },
  { name: 'Dr. Paola G.', country: 'Peru' },
  { name: 'Dr. Ricardo T.', country: 'Chile' },
  { name: 'Dr. Mariana P.', country: 'Ecuador' },
  { name: 'Dr. Sebastián V.', country: 'Uruguay' },
  { name: 'Dr. Lucía F.', country: 'Costa Rica' },
  { name: 'Dr. Fernando A.', country: 'Guatemala' },
  { name: 'Dr. Daniela C.', country: 'Panama' },
  { name: 'Dr. Gabriel N.', country: 'Dominican Republic' },
  { name: 'Dr. Isabela Q.', country: 'Bolivia' },
  { name: 'Dr. Martín H.', country: 'Paraguay' },
  { name: 'Dr. Sofía B.', country: 'Venezuela' },
];
